import Decimal from 'decimal.js';
import { ServiceCostType } from "./model";
import { utils } from "../../utils";
import { DiscountModel, DiscountType } from "../DiscountModel";
import { RoundPrices } from "../PriceIncreaseModel";
import pluralize from "../../utils/pluralize";
export function isFixedPrice(cost) {
  return cost.pricingType === ServiceCostType.Fixed;
}
export function isVariablePrice(cost) {
  return cost.pricingType === ServiceCostType.Variable;
}
export function isRangePrice(cost) {
  return cost.pricingType === ServiceCostType.Range;
}
export function price(cost) {
  if (cost.pricingType === ServiceCostType.Fixed) {
    return cost.price;
  } else if (cost.pricingType === ServiceCostType.Variable) {
    return cost.price;
  } else {
    return cost.maxPrice;
  }
}
export function minPrice(cost) {
  return cost.pricingType === ServiceCostType.Range ? cost.minPrice : null;
}
export function finalPrice(cost, quantity) {
  if (isRangePrice(cost)) {
    return utils.applyDiscount(cost.maxPrice, cost.discount);
  } else if (isFixedPrice(cost)) {
    return utils.applyDiscount(cost.price, cost.discount);
  } else {
    return utils.calculateFinalPrice(cost.price, cost.discount, quantity);
  }
}
export function applyDiscount(cost) {
  if (isFixedPrice(cost) || isVariablePrice(cost)) {
    return utils.applyDiscount(cost.price, cost.discount);
  } else {
    return utils.applyDiscount(cost.maxPrice, cost.discount);
  }
}
export function minPriceFinal(cost) {
  return cost.pricingType === ServiceCostType.Range && cost.minPrice ? utils.applyDiscount(cost.minPrice, cost.discount) : null;
}
export function discount(cost) {
  return cost.discount;
}
export function expiredDiscount(cost) {
  return cost.expiredDiscount;
}
export function discountPeriodExpiration(cost) {
  return cost.discount.expiration();
}
export function unitCap(cost) {
  return isVariablePrice(cost) ? cost.unitCap : null;
}
export function preapprovedControlledByAdvancedSettings(cost) {
  return isVariablePrice(cost) && (cost.preapprovedControlledByAdvancedSettings || false);
}
export function unitName(cost) {
  if (cost.pricingType === ServiceCostType.Fixed || cost.pricingType === ServiceCostType.Range) {
    return null;
  } else {
    return cost.unitName;
  }
}
export function priceIncrease(cost) {
  return cost.priceIncrease;
}
export function nextYearPrice(cost) {
  if (cost.priceIncrease.isNonPositiveIncrease()) {
    return null;
  }
  const rawResult = finalPrice(cost, 1).times(cost.priceIncrease.percentIncrease().plus(100)).div(100);
  switch (cost.priceIncrease.roundPrices()) {
    case RoundPrices.None:
      return rawResult.toDP(2).toNumber();
    case RoundPrices.Down1:
      return rawResult.floor().toNumber();
    case RoundPrices.Down5:
      return rawResult.minus(rawResult.mod(5)).toNumber();
    case RoundPrices.Down10:
      {
        return rawResult.minus(rawResult.mod(10)).toNumber();
      }
  }
}
export const defaultUnitName = 'hour';
export const defaultUnitNameShort = 'hr';
export function formattedUnitName(cost) {
  const un = unitName(cost);
  return un || defaultUnitName;
}
export function formattedUnitNameShort(cost) {
  const un = unitName(cost);
  return un || defaultUnitNameShort;
}
export function stringifyServiceCost(cost) {
  var _discount$amount, _discount$type;
  const applyDiscount = function applyDiscount(price) {
    let discount = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Decimal(0);
    let discountType = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DiscountType.Value;
    const discountedPrice = utils.applyDiscount(price, DiscountModel.make(discountType, discount));
    return utils.formatDollarAmount(discountedPrice);
  };
  const {
    discount,
    pricingType
  } = cost;
  const discountAmount = (_discount$amount = discount === null || discount === void 0 ? void 0 : discount.amount()) !== null && _discount$amount !== void 0 ? _discount$amount : new Decimal(0);
  const discountType = (_discount$type = discount === null || discount === void 0 ? void 0 : discount.type()) !== null && _discount$type !== void 0 ? _discount$type : DiscountType.Value;
  switch (pricingType) {
    case ServiceCostType.Fixed:
      return {
        costDisplay: applyDiscount(cost.price, discountAmount, discountType)
      };
    case ServiceCostType.Variable:
      {
        const unitCap = cost.unitCap;
        const unitName = formattedUnitName(cost);
        const unitCapDisplay = unitCap ? "".concat(unitCap, " ").concat(pluralize(unitName, unitCap.toNumber()), " preapproved") : undefined;
        return {
          costDisplay: "".concat(applyDiscount(cost.price, discountAmount, discountType), "/").concat(unitName),
          unitCapDisplay
        };
      }
    case ServiceCostType.Range:
      {
        if (cost.minPrice) {
          return {
            costDisplay: "".concat(applyDiscount(cost.minPrice, discountAmount, discountType), "\u2014").concat(applyDiscount(cost.maxPrice, discountAmount, discountType))
          };
        } else {
          return {
            costDisplay: "Up to ".concat(applyDiscount(cost.maxPrice, discountAmount, discountType))
          };
        }
      }
  }
}
export function formattedCost(cost) {
  const {
    costDisplay,
    unitCapDisplay
  } = stringifyServiceCost(cost);
  return unitCapDisplay ? "".concat(costDisplay, " \u2022 ").concat(unitCapDisplay) : "".concat(costDisplay);
}
export function match(cost, visitor) {
  switch (cost.pricingType) {
    case ServiceCostType.Fixed:
      return visitor.fixed(cost);
    case ServiceCostType.Variable:
      return visitor.variable(cost);
    case ServiceCostType.Range:
      return visitor.range(cost);
  }
}
export function isDiscountAndRateEqual(cost, costToCompare) {
  var _minPrice, _minPrice2, _discount, _costToCompare$discou, _costToCompare$discou2, _discount2, _costToCompare$discou3;
  const isEqual = cost.pricingType === costToCompare.pricingType && price(cost).equals(price(costToCompare)) && (isRangePrice(cost) ? (_minPrice = minPrice(cost)) === null || _minPrice === void 0 ? void 0 : _minPrice.equals((_minPrice2 = minPrice(costToCompare)) !== null && _minPrice2 !== void 0 ? _minPrice2 : new Decimal(0)) : true) && ((_discount = discount(cost)) === null || _discount === void 0 ? void 0 : _discount.amount().equals((_costToCompare$discou = (_costToCompare$discou2 = costToCompare.discount) === null || _costToCompare$discou2 === void 0 ? void 0 : _costToCompare$discou2.amount()) !== null && _costToCompare$discou !== void 0 ? _costToCompare$discou : new Decimal(0))) && ((_discount2 = discount(cost)) === null || _discount2 === void 0 ? void 0 : _discount2.type()) === ((_costToCompare$discou3 = costToCompare.discount) === null || _costToCompare$discou3 === void 0 ? void 0 : _costToCompare$discou3.type());
  return isEqual || false;
}